import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import slide1 from "@/assets/speed/sida-speed-1-ar-v2.webm.asset.json";
import slide2 from "@/assets/speed/sida-speed-2.webp.asset.json";
import slide3 from "@/assets/speed/sida-speed-3.webp.asset.json";
import slide4 from "@/assets/speed/sida-speed-4.webp.asset.json";
import slide1En from "@/assets/speed/sida-speed-1-en-animated.webp.asset.json";
import slide2En from "@/assets/speed/sida-speed-2-en.webp.asset.json";
import slide3En from "@/assets/speed/sida-speed-3-en.webp.asset.json";
import slide4En from "@/assets/speed/sida-speed-4-en.webp.asset.json";

type Slide = {
  src: string;
  srcEn: string;
  titleAr: string; titleEn: string; titleFr: string;
  descAr: string; descEn: string; descFr: string;
  altAr: string; altEn: string; altFr: string;
  animated?: boolean;
  video?: boolean;
};

const SLIDES: Slide[] = [
  {
    src: slide1.url,
    srcEn: slide1En.url,
    titleAr: "تكرار الوصف تلقائيًا", titleEn: "Auto-repeating description", titleFr: "Répétition automatique du libellé", // i18n-allow: tri-locale data / Urdu-only demo
    descAr: "اكتب بيان القيد مرة، ويظهر فورًا في كل السطور.", // i18n-allow: tri-locale data / Urdu-only demo
    descEn: "Type the journal narration once — it fills every line instantly.",
    descFr: "Saisissez le libellé une seule fois — il apparaît sur toutes les lignes.", // i18n-allow: tri-locale data / Urdu-only demo
    altAr: "شاشة قيد يومية في سيدا مع تكرار الوصف تلقائيًا", // i18n-allow: tri-locale data / Urdu-only demo
    altEn: "SIDA journal entry screen with auto-repeating narration",
    altFr: "Écran d'écriture SIDA avec libellé répété automatiquement", // i18n-allow: tri-locale data / Urdu-only demo
    animated: true,
    video: true,
  },
  {
    src: slide2.url,
    srcEn: slide2En.url,
    titleAr: "عقود الإيجار", titleEn: "Lease contracts", titleFr: "Contrats de bail", // i18n-allow: tri-locale data / Urdu-only demo
    descAr: "مسودة قيود تُولَّد وتُرحَّل تلقائيًا.", // i18n-allow: tri-locale data / Urdu-only demo
    descEn: "Draft journal entries generated and posted automatically.",
    descFr: "Écritures comptables en brouillon générées et comptabilisées automatiquement.", // i18n-allow: tri-locale data / Urdu-only demo
    altAr: "عقد إيجار مكتب في سيدا مع جدول سداد شهري تلقائي", // i18n-allow: tri-locale data / Urdu-only demo
    altEn: "SIDA office lease with an automatic monthly payment schedule",
    altFr: "Bail de bureau SIDA avec échéancier mensuel automatique", // i18n-allow: tri-locale data / Urdu-only demo
  },
  {
    src: slide3.url,
    srcEn: slide3En.url,
    titleAr: "المصروفات المدفوعة مقدمًا", titleEn: "Prepaid expenses", titleFr: "Charges payées d'avance", // i18n-allow: tri-locale data / Urdu-only demo
    descAr: "دورة إطفاء شهرية جاهزة تلقائيًا.", // i18n-allow: tri-locale data / Urdu-only demo
    descEn: "A ready-to-post monthly amortization cycle in one click.",
    descFr: "Un cycle d'amortissement mensuel prêt en un clic.", // i18n-allow: tri-locale data / Urdu-only demo
    altAr: "مصروف نظافة سنوي مع دورة إطفاء شهرية في سيدا", // i18n-allow: tri-locale data / Urdu-only demo
    altEn: "Annual cleaning contract with monthly amortization in SIDA",
    altFr: "Contrat annuel avec amortissement mensuel dans SIDA",
  },
  {
    src: slide4.url,
    srcEn: slide4En.url,
    titleAr: "المصروفات المستحقة", titleEn: "Accrued expenses", titleFr: "Charges à payer", // i18n-allow: tri-locale data / Urdu-only demo
    descAr: "قيد استحقاق يتكرر تلقائيًا كل شهر.", // i18n-allow: tri-locale data / Urdu-only demo
    descEn: "An accrual entry that recurs automatically every month.",
    descFr: "Une écriture de régularisation récurrente chaque mois.", // i18n-allow: tri-locale data / Urdu-only demo
    altAr: "مصروف كهرباء مستحق مع قيد استحقاق شهري متكرر في سيدا", // i18n-allow: tri-locale data / Urdu-only demo
    altEn: "Accrued electricity expense with monthly recurring entry in SIDA",
    altFr: "Charge d'électricité à payer avec écriture mensuelle récurrente dans SIDA", // i18n-allow: tri-locale data / Urdu-only demo
  },
];

const pick = (lang: string, ar: string, en: string, fr: string) =>
  lang.startsWith("ar") ? ar : lang.startsWith("fr") ? fr : en;

export function SpeedCarousel() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "ar").split("-")[0];
  const isRtl = i18n.dir() === "rtl";

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    direction: isRtl ? "rtl" : "ltr",
    align: "center",
    duration: 25,
  });
  const [selected, setSelected] = useState(0);
  const paused = useRef(false);
  const reduced = useRef(false);

  useEffect(() => {
    reduced.current = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  }, []);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Autoplay
  useEffect(() => {
    if (!emblaApi || reduced.current) return;
    const id = window.setInterval(() => {
      if (!paused.current && document.visibilityState === "visible") emblaApi.scrollNext();
    }, 6000);
    return () => window.clearInterval(id);
  }, [emblaApi]);

  // Arrow keys
  useEffect(() => {
    if (!emblaApi) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") isRtl ? emblaApi.scrollNext() : emblaApi.scrollPrev();
      else if (e.key === "ArrowRight") isRtl ? emblaApi.scrollPrev() : emblaApi.scrollNext();
    };
    const el = document.getElementById("sida-speed-carousel");
    el?.addEventListener("keydown", h as any);
    return () => el?.removeEventListener("keydown", h as any);
  }, [emblaApi, isRtl]);

  const prevLabel = pick(lang, "السابق", "Previous", "Précédent");
  const nextLabel = pick(lang, "التالي", "Next", "Suivant");

  return (
    <div
      id="sida-speed-carousel"
      dir={isRtl ? "rtl" : "ltr"}
      tabIndex={0}
      className="relative mx-auto w-full max-w-[1100px] outline-none"
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
      onFocus={() => (paused.current = true)}
      onBlur={() => (paused.current = false)}
      onTouchStart={() => (paused.current = true)}
      aria-roledescription="carousel"
    >
      {/* Ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-6 -z-10 rounded-[2.5rem] blur-3xl opacity-60"
        style={{ background: "radial-gradient(circle at 50% 50%, hsl(207 74% 42% / 0.18), transparent 70%)" }}
      />

      <div ref={emblaRef} className="overflow-hidden rounded-2xl">
        <div className="flex">
          {SLIDES.map((s, i) => {
            const title = pick(lang, s.titleAr, s.titleEn, s.titleFr);
            const desc = pick(lang, s.descAr, s.descEn, s.descFr);
            const alt = pick(lang, s.altAr, s.altEn, s.altFr);
            const mediaSrc = lang.startsWith("ar") ? s.src : s.srcEn;
            const active = i === selected;
            return (
              <div
                key={i}
                role="group"
                aria-roledescription="slide"
                aria-label={`${i + 1} / ${SLIDES.length}`}
                aria-hidden={!active}
                className="min-w-0 shrink-0 grow-0 basis-full px-2 sm:px-4"
              >
                <div className="text-center mb-4 sm:mb-6 space-y-1.5">
                  <h3 className="font-display text-xl sm:text-2xl md:text-3xl tracking-tight text-foreground">
                    {title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground">{desc}</p>
                </div>
                <div
                  className={cn(
                    "relative w-full max-w-3xl mx-auto rounded-2xl transition-all duration-500 ease-out",
                    "bg-gradient-to-b from-[#1C75BC]/[0.06] via-[#1C75BC]/[0.02] to-transparent",
                    "dark:from-[#1C75BC]/[0.14] dark:via-[#1C75BC]/[0.05] dark:to-transparent",
                    "p-2 sm:p-4 md:p-6",
                    active
                      ? "opacity-100 scale-100 shadow-[0_30px_80px_-30px_rgba(28,117,188,0.35)] dark:shadow-[0_30px_80px_-25px_rgba(17,197,239,0.35)]"
                      : "opacity-40 scale-[0.97]",
                  )}
                  style={{ aspectRatio: "2600 / 1640" }}
                >
                  {s.video && lang.startsWith("ar") ? (
                    <video
                      key={mediaSrc}
                      src={mediaSrc}
                      aria-label={alt}
                      width={2600}
                      height={1640}
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload={i === 0 ? "auto" : "metadata"}
                      className="h-full w-full object-cover rounded-xl bg-transparent"
                      style={{ background: "transparent" }}
                    />
                  ) : (
                    <img
                      src={mediaSrc}
                      alt={alt}
                      width={2600}
                      height={1640}
                      loading={i === 0 ? "eager" : "lazy"}
                      decoding="async"
                      fetchPriority={i === 0 ? "high" : "low" as any}
                      className="h-full w-full object-cover rounded-xl"
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Arrows */}
      <button
        type="button"
        aria-label={prevLabel}
        onClick={() => emblaApi?.scrollPrev()}
        className="absolute top-1/2 -translate-y-1/2 right-0 sm:-right-4 z-10 grid h-10 w-10 sm:h-12 sm:w-12 place-items-center rounded-full bg-background/90 border border-border shadow-lg backdrop-blur hover:bg-background hover:scale-105 transition"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
      <button
        type="button"
        aria-label={nextLabel}
        onClick={() => emblaApi?.scrollNext()}
        className="absolute top-1/2 -translate-y-1/2 left-0 sm:-left-4 z-10 grid h-10 w-10 sm:h-12 sm:w-12 place-items-center rounded-full bg-background/90 border border-border shadow-lg backdrop-blur hover:bg-background hover:scale-105 transition"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>

      {/* Dots */}
      <div className="mt-6 flex items-center justify-center gap-2">
        {SLIDES.map((_, i) => {
          const active = i === selected;
          return (
            <button
              key={i}
              type="button"
              aria-label={`${pick(lang, "الشريحة", "Slide", "Diapositive")} ${i + 1}`}
              aria-current={active ? "true" : undefined}
              onClick={() => emblaApi?.scrollTo(i)}
              className="grid h-11 w-11 place-items-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              <span
                className={cn(
                  "block h-2.5 rounded-full transition-all duration-300",
                  active
                    ? "w-8 bg-gradient-to-r from-[hsl(207_74%_42%)] to-[hsl(192_88%_50%)]"
                    : "w-2.5 bg-muted-foreground/40 hover:bg-muted-foreground/60",
                )}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
